<template>
  <funding-assessments-base :state="state">
    <template #headerAction>
      <div class="md:flex items-baseline">
        <div class="flex-1">
          <label for="lock_grading" class="sr-only">
            Lock grading?
          </label>
        </div>
        <div class="mt-6 md:ml-4 md:mt-0">
          <input
            id="lock_grading"
            v-model="lockGrading"
            type="checkbox"
            class="hidden"
            name="lock_grading"
            :disabled="loading"
          />
          <div
            class="border flex uppercase font-bold overflow-hidden cursor-pointer text-center text-sm"
          >
            <div
              class="w-full py-2 px-6 m-1 outline-none"
              tabindex="0"
              :class="{
                'bg-negative-600 text-white': lockGrading && !loading,
                'hover:bg-negative-200 focus:bg-negative-200 hover:text-negative-600':
                  !lockGrading && !loading,
                'bg-grey-600 text-white': loading && lockGrading
              }"
              @click.self="setLockGrading(true)"
              @keydown.enter="setLockGrading(true)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-6 h-6"
              >
                <path
                  d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10 0v-4c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-8z"
                />
              </svg>
              <span class="sr-only">Lock</span>
            </div>
            <div
              class="w-full py-2 px-8 m-1 outline-none"
              tabindex="0"
              :class="{
                'bg-positive-600 text-white': !lockGrading && !loading,
                'hover:bg-positive-200 focus:bg-positive-200 hover:text-positive-600':
                  lockGrading && !loading,
                'bg-grey-600 text-white': loading && !lockGrading
              }"
              @click.self="setLockGrading(false)"
              @keydown.enter="setLockGrading(false)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-6 h-6"
              >
                <path
                  d="M12 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v3h2v-3c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-4v14h18v-14h-12z"
                />
              </svg>
              <span class="sr-only">Unlock</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-if="!isSingleStream" class="border-b border-grey-300">
      <nav class="navigation container py-2">
        <ul class="flex items-center justify-center">
          <li class="navigation-item mr-2 text-xs font-bold">
            Filter:
          </li>
          <li
            v-for="stream in $page.stream_counts"
            :key="stream.key"
            class="navigation-item"
          >
            <label
              class="no-underline cursor-pointer font-semibold text-sm block border-2 rounded-full py-1 px-4 transition-all transition-fast"
              :class="
                selectedStream.length == 1 && isActive(stream.key)
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer'
              "
              :style="
                isActive(stream.key)
                  ? { ...getStreamColorsByKey(stream.key) }
                  : {}
              "
            >
              <input
                v-model="selectedStream"
                name="streams"
                type="checkbox"
                :value="stream.key || 'unknown'"
                class="sr-only"
                :disabled="selectedStream.length == 1 && isActive(stream.key)"
              />
              {{ stream.name }} ({{ stream.count }})
            </label>
          </li>
        </ul>
      </nav>
    </div>
    <div class="container pt-12">
      <div
        v-if="state.applications.length > 10"
        class="mb-8 flex items-baseline"
      >
        <div class="w-48 mr-4">
          <app-button @click="templateModal = true">
            Templates
          </app-button>
        </div>
        <p class="mr-4">
          Approval Template:
          <strong
            :class="!!emailApproved ? 'text-positive-900' : 'text-negative-900'"
            v-html="!!emailApproved ? '&check;' : '&times;'"
          />
        </p>
        <p class="mr-4">
          Rejection Template:
          <strong
            :class="!!emailRejected ? 'text-positive-900' : 'text-negative-900'"
            v-html="!!emailRejected ? '&check;' : '&times;'"
          />
        </p>
      </div>

      <funding-assessments-review-table
        :streams="selectedStream"
        :applications="state.applications"
      />

      <div class="mt-8 flex items-baseline">
        <div class="w-48 mr-4">
          <app-button @click="templateModal = true">
            Templates
          </app-button>
        </div>
        <p class="mr-4">
          Approval Template:
          <strong
            :class="!!emailApproved ? 'text-positive-900' : 'text-negative-900'"
            v-html="!!emailApproved ? '&check;' : '&times;'"
          />
        </p>
        <p class="mr-4">
          Rejection Template:
          <strong
            :class="!!emailRejected ? 'text-positive-900' : 'text-negative-900'"
            v-html="!!emailRejected ? '&check;' : '&times;'"
          />
        </p>
      </div>
    </div>
    <portal to="modals">
      <app-modal v-if="templateModal" @close="templateModal = false">
        <aside class="w-screen max-w-xl bg-white shadow-lg">
          <header class="px-8 pt-4 pb-6 border-b-2">
            <h2 class="mb-4">
              <span class="border-b-4 border-primary-500 inline-block pb-1"
                >Wave {{ state.funding_pot.id }} Review Emails</span
              >
            </h2>
            <p class="max-w-md">
              The content defined in each of these templates will be used as a
              base template when notifing applicants. You can assign
              personalised comments to each application in the review table
              which will be appended with each notification email.
            </p>
          </header>
          <div class="flex">
            <div class="border-r flex-1 focus-within:bg-primary-50">
              <label
                for="temp_accept"
                class="flex items-center font-bold px-8 py-2 border-b-2"
              >
                <icon icon="check" class="w-6 h-6 icons-positive mr-4" />
                Approval Email
              </label>
              <textarea
                id="temp_accept"
                v-model="emailApproved"
                :disabled="loading"
                class="px-8 py-4 w-full block h-64 resize-none focus:outline-none bg-transparent"
                @blur="updateTemplates"
              >
              </textarea>
              <div class="mx-8 my-2 border-t py-2">
                <p>
                  <em
                    >Individual comments, if assigned will appear, after the
                    content outlined above.</em
                  >
                </p>
              </div>
            </div>
            <div class="border-l flex-1 focus-within:bg-primary-50">
              <label
                for="temp_reject"
                class="flex items-center font-bold px-8 py-2 border-b-2"
              >
                <icon icon="important" class="w-6 h-6 icons-negative mr-4" />
                Rejection Email
              </label>
              <textarea
                id="temp_reject"
                v-model="emailRejected"
                :disabled="loading"
                class="px-8 py-4 w-full block h-64 resize-none focus:outline-none bg-transparent"
                @blur="updateTemplates"
              >
              </textarea>
              <div class="mx-8 my-2 border-t py-2">
                <p>
                  <em
                    >Individual comments, if assigned will appear, after the
                    content outlined above.</em
                  >
                </p>
              </div>
            </div>
          </div>
          <footer class="border-t-2 px-8 py-4">
            <div class="w-48 ml-auto">
              <app-button @click="templateModal = false">
                Save & Exit
              </app-button>
            </div>
          </footer>
        </aside>
      </app-modal>
    </portal>
  </funding-assessments-base>
</template>

<script>
import { competitionStreamsMixin } from "../mixins/competitionStreams";

export default {
  name: "FundingAssessmentsReview",
  mixins: [competitionStreamsMixin],
  data() {
    return {
      loading: false,
      lockGrading: false,
      templateModal: false,
      selectedStream: [],
      emailApproved: this.state.marking_phase.approved_email_template ?? "",
      emailRejected: this.state.marking_phase.rejected_email_template ?? ""
    };
  },
  computed: {
    funding_pot() {
      return this.state.funding_pot;
    }
  },
  watch: {
    lockGrading(newValue) {
      if (this.loading) return;
      this.loading = true;
      this.$axios
        .post(
          route("funding.assessments.phase.locking", [
            this.state.funding_pot.id,
            this.state.marking_phase.id
          ]),
          {
            lock: newValue
          }
        )
        .then(() => {})
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(error);
          this.lockGrading = !newValue;
        })
        .then(() => (this.loading = false));
    }
  },
  created() {
    this.lockGrading = this.state.marking_phase.locked || false;
    this.assessments = this.state.assessments?.map(this.assignScore);
    document.title = `${this.state.marking_phase.name} Grading :: ${document.title}`;
  },
  methods: {
    assignScore(assessment) {
      const additionalKeys = {
        totalScore: 0,
        totalPercentage: 0,
        recommendedTotal: 0
      };

      for (
        let index = 0;
        index < assessment.all_assessor_grades_current_phase.length;
        index++
      ) {
        const currentAssessor =
          assessment.all_assessor_grades_current_phase[index];
        additionalKeys.totalScore += currentAssessor.total;
        additionalKeys.totalPercentage += currentAssessor.percentage;
        if (currentAssessor.recommended) additionalKeys.recommendedTotal++;
      }

      additionalKeys.totalPercentage = parseFloat(
        (additionalKeys.totalPercentage / assessment.assessors.length).toFixed(
          2
        )
      );

      return {
        ...assessment,
        ...additionalKeys
      };
    },
    updateTemplates() {
      if (this.loading) return;
      this.loading = true;
      axios({
        method: "post",
        url: this.route("funding.assessments.phase.email-templates", [
          this.state.funding_pot.id,
          this.state.marking_phase.id
        ]),
        data: {
          approved_email_template: this.emailApproved.trim(),
          rejected_email_template: this.emailRejected.trim()
        }
      }).then(() => (this.loading = false));
    },
    setLockGrading(value) {
      if (this.loading) return;
      this.lockGrading = value;
    },
    isActive(stream) {
      return this.selectedStream.includes(stream);
    }
  },
  mounted() {
    this.selectedStream = this.$page.competition_streams?.map(s => s.key) ?? [];
  }
};
</script>
